import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import VoicePlayer from "@/components/common/VoicePlayer";
import { SURVEY_QUESTIONS } from "@/lib/survey";
import { KNOWLEDGE_CATEGORIES } from "@/lib/knowledge";
import MiniEditor from "@/components/common/MiniEditor";

const ORGAN_LABELS = { kidney: "Kidney", liver: "Liver", heart: "Heart", lung: "Lung", pancreas: "Pancreas", intestine: "Intestine", other: "Other" };

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => { if (ready && !user) navigate("/admin"); }, [ready, user, navigate]);
  if (!ready || !user) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-canvas">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <span className="font-bold text-sm tracking-tight">MTJ ADMIN</span>
            <nav className="flex gap-1">
              {["overview", "journeys", "centers", "knowledge", "feedback"].map((t) => (
                <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
                  className={`px-3 py-1.5 text-sm rounded capitalize transition-colors ${tab === t ? "bg-ink text-white" : "text-neutral-600 hover:text-ink"}`}>
                  {t}
                </button>
              ))}
            </nav>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin"); }} className="text-sm text-neutral-500 hover:text-ink">Logout</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === "overview" && <Overview />}
        {tab === "journeys" && <Journeys />}
        {tab === "centers" && <Centres />}
        {tab === "feedback" && <Feedback />}
        {tab === "knowledge" && <Articles />}
      </main>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-neutral-500">Loading…</p>;
  const cards = [
    ["Transplant centers", s.centres], ["Visitors (total)", s.visits_total ?? 0], ["Visitors today", s.visits_today ?? 0], ["Total journeys", s.total_journeys], ["Published / approved", s.approved],
    ["Pending moderation", s.pending], ["Voice stories", s.voice],
    ["Text stories", s.text], ["Flagged", s.flagged], ["Hospital responses", s.hospital_responses],
    ["States represented", s.states_represented], ["Languages", s.languages_represented],
  ];
  return (
    <div>
      <h1 className="text-3xl font-light tracking-tight text-ink mb-8">Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(([label, val]) => (
          <div key={label} className="bg-white border border-neutral-200 p-5">
            <div className="text-3xl font-light font-mono text-ink">{val}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mt-2">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Journeys() {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/journeys${status ? `?status=${status}` : ""}`).then((r) => setList(r.data));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const open = (id) => api.get(`/admin/journeys/${id}`).then((r) => setSel(r.data));

  const act = async (id, body) => {
    await api.put(`/admin/journeys/${id}`, body);
    toast.success("Updated");
    load();
    if (sel && sel.id === id) open(id);
  };
  const del = async (id) => {
    await api.delete(`/admin/journeys/${id}`);
    toast.success("Deleted"); setSel(null); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-light tracking-tight text-ink">Journeys</h1>
        <div className="flex gap-1">
          {["pending", "needs_review", "approved", "rejected", ""].map((st) => (
            <button key={st || "all"} data-testid={`jstatus-${st || "all"}`} onClick={() => setStatus(st)}
              className={`px-3 py-1.5 text-xs rounded capitalize transition-colors ${status === st ? "bg-ink text-white" : "bg-white border border-neutral-200 text-neutral-600"}`}>
              {st ? st.replace("_", " ") : "all"}
            </button>
          ))}
        </div>
      </div>

      {list.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-10 text-center text-neutral-500">No journeys in this view.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            {list.map((j) => (
              <button key={j.id} data-testid={`admin-journey-${j.id}`} onClick={() => open(j.id)}
                className={`w-full text-left bg-white border p-4 transition-colors ${sel?.id === j.id ? "border-ink" : "border-neutral-200 hover:border-neutral-400"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-mono uppercase tracking-wider text-rust">
                    {ORGAN_LABELS[j.organ]} · {j.journey_type}
                  </span>
                  {j.flagged && <span className="text-[10px] font-mono bg-rust text-white px-2 py-0.5 rounded">FLAGGED</span>}
                </div>
                <p className="text-sm text-neutral-700 mt-2 line-clamp-4">{(j.story_text || j.transcript || "Voice journey").slice(0, 140)}</p>
                <div className="text-[11px] font-mono text-neutral-400 mt-2">{j.public_label} · {j.state_name} · {j.moderation_status}{j.has_audio ? " · voice" : ""} · 👍 {j.feedback_up || 0} 👎 {j.feedback_down || 0}</div>
              </button>
            ))}
          </div>

          <div className="lg:sticky lg:top-24 self-start">
            {!sel ? (
              <div className="border border-dashed border-neutral-300 p-10 text-center text-neutral-500">Select a journey to review.</div>
            ) : (
              <div className="bg-white border border-neutral-200 p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium">{sel.public_label}</h2>
                </div>
                <div className="text-[11px] font-mono text-neutral-500">
                  {ORGAN_LABELS[sel.organ]} · {sel.state_name} · {sel.journey_year} · {sel.language} · 👍 {sel.feedback_up || 0} 👎 {sel.feedback_down || 0}
                  {sel.hospital_name && ` · ${sel.hospital_name}`}
                </div>
                {sel.has_audio && sel.audio_url && <VoicePlayer src={sel.audio_url} duration={sel.audio_duration} />}
                {(sel.story_text || sel.transcript) && (
                  <div className="text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap bg-neutral-50 p-4 border border-neutral-100 max-h-56 overflow-auto">
                    {sel.story_text || sel.transcript}
                  </div>
                )}
                {sel.images && sel.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {sel.images.map((src, i) => (
                      <img key={i} src={src} alt={`photo ${i + 1}`} className="w-full aspect-square object-cover border border-neutral-200" />
                    ))}
                  </div>
                )}
                {sel.lessons && <div className="text-sm text-neutral-700"><span className="font-medium">Lessons: </span>{sel.lessons}</div>}

                {sel.survey && Object.keys(sel.survey).length > 0 && (
                  <div data-testid="admin-survey-block" className="border border-neutral-200 bg-[#FAF8F5] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-3">
                      Experience survey · keep for hospital analytics
                    </div>
                    <dl className="space-y-3 max-h-72 overflow-auto pr-1">
                      {SURVEY_QUESTIONS.filter((sq) => {
                        const v = sel.survey[sq.key];
                        return Array.isArray(v) ? v.length > 0 : !!v;
                      }).map((sq) => (
                        <div key={sq.key} data-testid={`admin-survey-${sq.key}`}>
                          <dt className="text-xs text-neutral-500 leading-snug">{sq.q}</dt>
                          <dd className="text-sm font-medium text-ink mt-0.5">
                            {Array.isArray(sel.survey[sq.key]) ? sel.survey[sq.key].join(" · ") : sel.survey[sq.key]}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {(sel.contributor_first_name || sel.contributor_name || sel.contributor_email || sel.contributor_phone) && (
                  <div className="text-xs font-mono text-neutral-500 bg-neutral-100 p-3 border border-neutral-200">
                    <div className="uppercase tracking-widest text-[10px] text-neutral-400 mb-1">Private contact — never public · for future follow-up</div>
                    {(sel.contributor_first_name || sel.contributor_last_name) && <div>Name: {[sel.contributor_first_name, sel.contributor_last_name].filter(Boolean).join(" ")}</div>}
                    {!sel.contributor_first_name && sel.contributor_name && <div>Name: {sel.contributor_name}</div>}
                    {sel.identity_preference && sel.identity_preference !== "anonymous" && <div>Public identity: {sel.identity_preference === "full_name" ? "Full name" : "First name"}</div>}
                    {sel.contributor_email && <div>Email: {sel.contributor_email}</div>}
                    {sel.contributor_phone && <div>Phone: {sel.contributor_phone}</div>}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-100">
                  <Act testId="act-approve" onClick={() => act(sel.id, { moderation_status: "approved", published: true })}>Approve & publish</Act>
                  <Act testId="act-reject" onClick={() => act(sel.id, { moderation_status: "rejected", published: false })}>Reject</Act>
                  <Act testId="act-review" onClick={() => act(sel.id, { moderation_status: "needs_review" })}>Needs review</Act>
                  <Act testId="act-flag" onClick={() => act(sel.id, { flagged: !sel.flagged })}>{sel.flagged ? "Unflag" : "Flag sensitive"}</Act>
                  <Act testId="act-unpublish" onClick={() => act(sel.id, { published: !sel.published })}>{sel.published ? "Unpublish" : "Publish"}</Act>
                </div>

                <HospitalResponse journey={sel} onSave={(txt) => act(sel.id, { hospital_response: txt })} />

                <button data-testid="act-delete" onClick={() => del(sel.id)} className="text-xs text-rust hover:text-rust-hover">Delete journey</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Feedback() {
  const [list, setList] = useState([]);
  const load = useCallback(() => { api.get("/admin/feedback").then((r) => setList(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await api.delete(`/admin/feedback/${id}`); toast.success("Deleted"); load(); };
  return (
    <div>
      <h1 className="text-3xl font-light tracking-tight text-ink mb-8">Feedback</h1>
      {list.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-10 text-center text-neutral-500">No feedback received yet.</div>
      ) : (
        <div className="space-y-3">
          {list.map((f) => (
            <div key={f.id} data-testid={`feedback-${f.id}`} className="bg-white border border-neutral-200 p-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap">{f.message}</p>
                <div className="text-[11px] font-mono text-neutral-400 mt-2">
                  {f.name || "Anonymous"}{f.email ? ` · ${f.email}` : ""} · {new Date(f.created_at).toLocaleString()}
                </div>
              </div>
              <button data-testid={`feedback-delete-${f.id}`} onClick={() => del(f.id)} className="text-xs text-rust hover:text-rust-hover shrink-0">Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_ARTICLE = { title: "", description: "", category: "Before Transplant", featured_image: "", content: "", author: "Transplant Story Editorial", published_date: "", sources: "", seo_title: "", seo_description: "", status: "draft" };

function compressImageFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1400;
        let { width, height } = img;
        if (width > max || height > max) {
          if (width > height) { height = Math.round((height * max) / width); width = max; }
          else { width = Math.round((width * max) / height); height = max; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function Articles() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => { api.get("/admin/articles").then((r) => setList(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (publishNow) => {
    setSaving(true);
    try {
      const body = { ...editing, status: publishNow ? "published" : editing.status };
      if (editing.id) await api.put(`/admin/articles/${editing.id}`, body);
      else await api.post("/admin/articles", body);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Could not save. Title is required."); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/admin/articles/${id}`); toast.success("Deleted"); setEditing(null); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-light tracking-tight text-ink">Knowledge</h1>
        <button data-testid="article-add" onClick={() => setEditing({ ...EMPTY_ARTICLE })} className="bg-ink text-white px-4 py-2 text-sm hover:bg-rust transition-colors">Add article</button>
      </div>

      {list.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-10 text-center text-neutral-500">No articles yet.</div>
      ) : (
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
          {list.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-4 p-4" data-testid={`admin-article-${a.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-ink truncate">{a.title}</div>
                <div className="text-[11px] font-mono text-neutral-500 mt-0.5">
                  {a.category} · {(a.updated_at || "").slice(0, 10)} · {a.reading_time} min
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded uppercase ${a.status === "published" ? "bg-ink text-white" : "bg-neutral-200 text-neutral-600"}`}>
                  {a.status}
                </span>
                <button data-testid={`article-edit-${a.id}`} onClick={() => setEditing({ ...EMPTY_ARTICLE, ...a })} className="text-xs border border-neutral-300 px-3 py-1.5 hover:border-ink">Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-medium">{editing.id ? "Edit article" : "Add article"}</h2>
            <Field label="Article title">
              <input data-testid="article-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
            </Field>
            <Field label="Short description">
              <textarea data-testid="article-desc" rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust resize-none" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Category">
                <input data-testid="article-category" list="knowledge-cats" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
                <datalist id="knowledge-cats">{KNOWLEDGE_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
              </Field>
              <Field label="Author">
                <input data-testid="article-author" value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
              </Field>
            </div>
            <Field label="Featured image">
              {editing.featured_image && (
                <div className="flex items-center gap-3 mb-2">
                  <img src={editing.featured_image} alt="" className="w-32 h-20 object-cover border border-neutral-200" />
                  <button type="button" onClick={() => setEditing({ ...editing, featured_image: "" })} className="text-xs text-rust">Remove</button>
                </div>
              )}
              <input type="file" accept="image/*" data-testid="article-image"
                onChange={async (e) => { if (e.target.files[0]) setEditing({ ...editing, featured_image: await compressImageFile(e.target.files[0]) }); }}
                className="text-xs text-neutral-600" />
            </Field>
            <Field label="Article content">
              <MiniEditor testId="article-content" value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Published date">
                <input type="date" data-testid="article-date" value={editing.published_date || ""} onChange={(e) => setEditing({ ...editing, published_date: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
              </Field>
              <Field label="Status">
                <select data-testid="article-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust bg-white">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </Field>
            </div>
            <Field label="Sources">
              <textarea data-testid="article-sources" rows={2} value={editing.sources || ""} onChange={(e) => setEditing({ ...editing, sources: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust resize-none" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="SEO title (optional)">
                <input data-testid="article-seo-title" value={editing.seo_title || ""} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
              </Field>
              <Field label="SEO description (optional)">
                <input data-testid="article-seo-desc" value={editing.seo_description || ""} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
              </Field>
            </div>
            <div className="flex items-center justify-between pt-2">
              {editing.id
                ? <button data-testid="article-delete" onClick={() => del(editing.id)} className="text-xs text-rust hover:text-rust-hover">Delete article</button>
                : <span />}
              <div className="flex gap-2">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-neutral-300">Cancel</button>
                <button data-testid="article-save-draft" disabled={saving} onClick={() => save(false)} className="px-4 py-2 text-sm border border-ink hover:bg-neutral-50 disabled:opacity-40">Save draft</button>
                <button data-testid="article-publish" disabled={saving} onClick={() => save(true)} className="px-4 py-2 text-sm bg-ink text-white hover:bg-rust transition-colors disabled:opacity-40">Publish</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Act({ children, onClick, testId }) {
  return <button data-testid={testId} onClick={onClick} className="px-3 py-1.5 text-xs border border-neutral-300 hover:border-ink hover:bg-neutral-50 transition-colors">{children}</button>;
}

function HospitalResponse({ journey, onSave }) {
  const [txt, setTxt] = useState(journey.hospital_response || "");
  useEffect(() => { setTxt(journey.hospital_response || ""); }, [journey.id]);
  return (
    <div className="pt-2 border-t border-neutral-100">
      <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-400 mb-2">Hospital response</label>
      <textarea data-testid="hospital-response-input" value={txt} onChange={(e) => setTxt(e.target.value)} rows={2}
        className="w-full border border-neutral-300 p-2 text-sm outline-none focus:border-rust" />
      <button data-testid="hospital-response-save" onClick={() => onSave(txt)} className="mt-2 px-3 py-1.5 text-xs bg-ink text-white hover:bg-rust transition-colors">Save response</button>
    </div>
  );
}

const EMPTY_CENTRE = { hospital_name: "", city: "", district: "", state_code: "", organs: [], official_website: "", hospital_logo_url: "", notto_status: "Directory listed" };
const ALL_ORGANS = ["kidney", "liver", "heart", "lung", "pancreas", "intestine"];
const STATUSES = ["NOTTO listed", "Government registered", "Approved transplant center", "Registered transplant center", "Directory listed"];

function Centres() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [states, setStates] = useState([]);
  const [organFilter, setOrganFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (organFilter) params.set("organ", organFilter);
    api.get(`/admin/centres?${params.toString()}`).then((r) => setList(r.data));
  }, [q, organFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/states").then((r) => setStates(r.data)); }, []);

  const save = async () => {
    const body = { ...editing, state_code: editing.state_code };
    try {
      if (editing.id) await api.put(`/admin/centres/${editing.id}`, body);
      else await api.post("/admin/centres", body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error("Could not save. Check required fields."); }
  };
  const del = async (id) => { await api.delete(`/admin/centres/${id}`); toast.success("Deleted"); load(); };

  const toggleOrgan = (o) => {
    setEditing((e) => ({ ...e, organs: e.organs.includes(o) ? e.organs.filter((x) => x !== o) : [...e.organs, o] }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4">
        <h1 className="text-3xl font-light tracking-tight text-ink">Centers</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="centre-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hospital"
            className="border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-rust" />
          <select data-testid="centre-organ-filter" value={organFilter} onChange={(e) => setOrganFilter(e.target.value)}
            className="border border-neutral-300 px-3 py-2 text-sm bg-white outline-none focus:border-rust capitalize">
            <option value="">All organs</option>
            {ALL_ORGANS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <span className="text-xs font-mono text-neutral-400">{list.length}</span>
          <button data-testid="centre-add" onClick={() => setEditing({ ...EMPTY_CENTRE })} className="bg-ink text-white px-4 py-2 text-sm hover:bg-rust transition-colors">Add centre</button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
        {list.map((c) => (
          <div key={c.id} className="flex items-center justify-between p-4" data-testid={`admin-centre-${c.id}`}>
            <div>
              <div className="font-medium text-ink">{c.hospital_name}</div>
              <div className="text-[11px] font-mono text-neutral-500">{c.city}, {c.state_name} · {c.notto_status} · {(c.organs || []).join(", ")}</div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`centre-edit-${c.id}`} onClick={() => setEditing({ ...EMPTY_CENTRE, ...c })} className="text-xs border border-neutral-300 px-3 py-1.5 hover:border-ink">Edit</button>
              <button data-testid={`centre-delete-${c.id}`} onClick={() => del(c.id)} className="text-xs text-rust hover:text-rust-hover px-2">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-medium">{editing.id ? "Edit center" : "Add center"}</h2>
            <Field label="Hospital name"><input data-testid="centre-name" value={editing.hospital_name} onChange={(e) => setEditing({ ...editing, hospital_name: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City"><input data-testid="centre-city" value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" /></Field>
              <Field label="State">
                <select data-testid="centre-state" value={editing.state_code} onChange={(e) => setEditing({ ...editing, state_code: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust bg-white">
                  <option value="">Select</option>
                  {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Status / source label">
              <select data-testid="centre-status" value={editing.notto_status} onChange={(e) => setEditing({ ...editing, notto_status: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust bg-white">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Official website"><input data-testid="centre-website" value={editing.official_website || ""} onChange={(e) => setEditing({ ...editing, official_website: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" /></Field>
            <Field label="Logo URL"><input data-testid="centre-logo" value={editing.hospital_logo_url || ""} onChange={(e) => setEditing({ ...editing, hospital_logo_url: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-rust" /></Field>
            <Field label="Organ programs">
              <div className="flex flex-wrap gap-2">
                {ALL_ORGANS.map((o) => (
                  <button key={o} type="button" data-testid={`centre-organ-${o}`} onClick={() => toggleOrgan(o)}
                    className={`px-3 py-1.5 text-xs rounded-full border capitalize ${editing.organs.includes(o) ? "bg-ink text-white border-ink" : "border-neutral-300"}`}>{o}</button>
                ))}
              </div>
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-neutral-300">Cancel</button>
              <button data-testid="centre-save" onClick={save} className="px-4 py-2 text-sm bg-ink text-white hover:bg-rust transition-colors">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
